import fp from 'lodash/fp';
import React, { ComponentType, useState, useCallback, useMemo, useEffect, FC } from 'react';
import DayPicker, { DayPickerProps, CaptionElementProps } from 'react-day-picker';
import 'react-day-picker/lib/style.css';

import { cn } from '../../utils';
import { Select2 } from '../Select2';
import styles from './index.module.scss';


export type CalendarProps = Pick<DayPickerProps, 'onFocus' | 'onBlur' | 'modifiers' | 'disabledDays'> & {
    value?: Date;
    onChange: (value: Date) => void;
    locale: 'en' | 'ru';
};

type LocaleProps = Partial<Pick<DayPickerProps, (
    'locale' |
    'months' |
    'weekdaysShort' |
    'firstDayOfWeek'
)>>;

const localizationProps: { [KLang in CalendarProps['locale']]: LocaleProps } = {
    en: {
        locale: 'en',
        firstDayOfWeek: 0,
    },
    ru: {
        locale: 'ru',
        months: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
        weekdaysShort: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        firstDayOfWeek: 1,
    },
};

type CaptionProps = Partial<CaptionElementProps> & {
    onChange: (month: Date) => void;
};

const CalendarCaption: ComponentType<CaptionProps> = ({ date, months, localeUtils, onChange }) => {
    const month = date.getMonth();
    const year = date.getFullYear();

    const monthData = useMemo(
        () => (months || localeUtils.getMonths()).map((label, index) => ({
            label,
            value: index,
        })),
        [localeUtils, months],
    );
    const updateMonth = useCallback(
        (value: number) => onChange(new Date(year, value, 1)),
        [onChange, year],
    );

    const yearData = useMemo(
        () => fp.range(0, 181).map(offset => ({
            label: `${year - 90 + offset}`,
            value: year - 90 + offset,
        })),
        [year],
    );
    const updateYear = useCallback(
        (value: number) => onChange(new Date(value, month, 1)),
        [onChange, month],
    );

    return (
        <div className={styles.caption}>
            <Select2
                className={styles.select}
                variant='standalone'
                options={monthData}
                value={month}
                onChange={updateMonth}
            />
            <Select2
                className={cn(styles.select, styles.years)}
                variant='standalone'
                ellipsis={false}
                options={yearData}
                value={year}
                onChange={updateYear}
            />
        </div>
    );
};

export const Calendar: FC<CalendarProps> = ({
    value,
    onChange,
    locale,
    modifiers = {},
    ...props
}) => {
    const [month, setMonth] = useState<Date>(null);
    const captionElement = useMemo<DayPickerProps['captionElement']>(
        () => props => <CalendarCaption onChange={setMonth} {...props} />,
        [],
    );
    useEffect(() => setMonth(null), [value]);

    const onDayClick = useCallback<DayPickerProps['onDayClick']>(
        (day, { disabled }) => {
            if (!disabled)
                onChange(day);
        },
        [onChange],
    );

    return (
        <DayPicker
            month={month || value}
            selectedDays={value}
            onDayClick={onDayClick}
            className={styles.calendar}
            captionElement={captionElement}
            modifiers={{
                weekend: { daysOfWeek: [0, 6] },
                ...modifiers,
            }}
            {...props}
            {...localizationProps[locale]}
        />
    );
};
